/**
 * W1-11b (app-shell-ui): hash routing and workspace switching (`services.nav`; DESIGN.md §3.1, ARCHITECTURE.md §6.2).
 *
 * Routes: `#/` Library, `#/settings[/<section>]`, `#/p/<projectId>/<timing|chart|publish>`; any other hash is left
 * to the routes modules register (dev pages). Hash routes make reload and browser Back work:
 *  - opening a project, the Library and Settings push a history entry; switching workspaces and moving between
 *    settings sections replace it (Back leaves the editor or the settings page instead of cycling tabs);
 *  - the hash is the source of truth for navigation; the editor store's `workspace` follows it, and a workspace
 *    changed directly in the store (view-state restore) is written back to the hash;
 *  - a project route whose project is not open asks the session to open it (reload, Back, a typed link); the
 *    Library route closes the open project; Settings keeps it open (Back returns to it).
 * Project opening and closing are the project session's job (W1-16 projectSession.ts), attached with
 * `attachSession({ open, close })`; without a session the navigation still happens and the editor route shows its
 * "opening" state.
 *
 * W3-01 (UX-012): a workspace named in the address wins over the one remembered with the project's view state.
 * While a project opens, workspace changes in the editor store (the view-state restore) are not written to the
 * hash; once it is open, the address's workspace is applied, and only an address without one (a Library open, a
 * bare `#/p/<id>`) takes the remembered workspace.
 */

import type { ProjectId } from '../core/model/types';
import type { MessageRef, Unsubscribe } from '../core/util/types';
import type { EditorStoreApi, WorkspaceId } from '../state/types';
import type { NavService } from './registry/types';

export type AppRoute =
  | { readonly kind: 'library' }
  | { readonly kind: 'settings'; readonly section: string | null }
  | { readonly kind: 'project'; readonly id: ProjectId; readonly workspace: WorkspaceId | null }
  | { readonly kind: 'other'; readonly hash: string };

export const WORKSPACE_IDS: readonly WorkspaceId[] = ['timing', 'chart', 'publish'];

export function isWorkspaceId(v: unknown): v is WorkspaceId {
  return typeof v === 'string' && (WORKSPACE_IDS as readonly string[]).includes(v);
}

/** '#/a/b?x' -> ['a', 'b'] (decoded; query and empty segments dropped). */
export function hashSegments(hash: string): string[] {
  let h = hash.startsWith('#') ? hash.slice(1) : hash;
  const q = h.indexOf('?');
  if (q >= 0) h = h.slice(0, q);
  return h
    .split('/')
    .filter((s) => s !== '')
    .map((s) => {
      try {
        return decodeURIComponent(s);
      } catch {
        return s;
      }
    });
}

/** Canonical form of a hash ('' and '#' are the Library '#/'). */
export function normalizeHash(hash: string): string {
  const segs = hashSegments(hash);
  return segs.length === 0 ? '#/' : `#/${segs.map((s) => encodeURIComponent(s)).join('/')}`;
}

export function parseRoute(hash: string): AppRoute {
  const segs = hashSegments(hash);
  const [first, second, third] = segs;
  if (first === undefined) return { kind: 'library' };
  if (first === 'settings') return { kind: 'settings', section: segs.length > 1 ? segs.slice(1).join('/') : null };
  if (first === 'p' && second !== undefined && second !== '') {
    return { kind: 'project', id: second as ProjectId, workspace: isWorkspaceId(third) ? third : null };
  }
  return { kind: 'other', hash: normalizeHash(hash) };
}

export function formatRoute(r: AppRoute): string {
  switch (r.kind) {
    case 'library':
      return '#/';
    case 'settings':
      return r.section === null || r.section === ''
        ? '#/settings'
        : `#/settings/${r.section
            .split('/')
            .map((s) => encodeURIComponent(s))
            .join('/')}`;
    case 'project':
      return `#/p/${encodeURIComponent(r.id)}/${r.workspace ?? 'chart'}`;
    case 'other':
      return r.hash;
  }
}

export interface NavSession {
  /** Open the project (persist -> stores -> engine ...). Rejects on failure (e.g. locked in another tab). */
  open(id: ProjectId): Promise<void>;
  /** Flush and close the open project. */
  close(): Promise<void>;
}

export interface NavServiceDeps {
  readonly window?: Window;
  readonly editor: () => EditorStoreApi;
  /** The open project's id (default: the editor store's projectId). */
  readonly currentProject?: () => ProjectId | null;
  /**
   * Shows navigation failures. `origin` tells an explicit `openProject` ('open') from a hash-driven open or route
   * work ('route'): the former also rejects to its caller, the latter only reports here.
   */
  readonly onError?: (reason: MessageRef | null, error: unknown, origin: 'open' | 'route') => void;
}

export interface ShellNavService extends NavService {
  route(): AppRoute;
  readonly hash: string;
  subscribe(cb: () => void): Unsubscribe;
  /** The project session (W1-16); returns the detach function. */
  attachSession(session: NavSession): () => void;
  /** Start following the hash (hashchange + editor workspace); returns the stop function. Idempotent. */
  start(): () => void;
  /** A project route is being opened (the editor shows its "opening" state). */
  readonly opening: ProjectId | null;
  /** Last failure to open a project route (cleared by the next attempt: navigation or `openProject`). */
  readonly openError: unknown;
}

export function isShellNavService(nav: NavService): nav is ShellNavService {
  const x = nav as Partial<ShellNavService>;
  return typeof x.route === 'function' && typeof x.subscribe === 'function' && typeof x.start === 'function';
}

export function createNavService(deps: NavServiceDeps): ShellNavService {
  const win = deps.window ?? window;
  const listeners = new Set<() => void>();
  let session: NavSession | null = null;
  let stop: (() => void) | null = null;
  let opening: ProjectId | null = null;
  let openError: unknown = null;
  /** Whether the open attempt `sync` runs now was asked for by `openProject` ('open') or by the hash ('route'). */
  let openOrigin: 'open' | 'route' = 'route';
  /** Serialises session work (open/close run one at a time, in order). */
  let chain: Promise<void> = Promise.resolve();
  let lastHash = normalizeHash(win.location.hash);

  const notify = (): void => {
    for (const cb of [...listeners]) cb();
  };
  const currentProject = (): ProjectId | null =>
    deps.currentProject !== undefined ? deps.currentProject() : deps.editor().getState().projectId;

  const writeHash = (hash: string, mode: 'push' | 'replace'): void => {
    const target = normalizeHash(hash);
    if (normalizeHash(win.location.hash) === target) return;
    const url = `${win.location.pathname}${win.location.search}${target}`;
    if (mode === 'push') win.history.pushState(null, '', url);
    else win.history.replaceState(null, '', url);
    void sync();
  };

  const enqueue = (work: () => Promise<void>): Promise<void> => {
    const next = chain.then(work, work);
    chain = next.catch(() => undefined);
    return next;
  };

  /** Bring the app in line with the current hash. */
  const sync = (): Promise<void> => {
    const hash = normalizeHash(win.location.hash);
    const changed = hash !== lastHash;
    lastHash = hash;
    const route = parseRoute(hash);
    if (changed) notify();
    if (route.kind === 'project') {
      const editor = deps.editor().getState();
      const isOpen = currentProject() === route.id;
      if (route.workspace === null && (isOpen || session === null)) {
        writeHash(formatRoute({ ...route, workspace: isOpen ? editor.workspace : 'chart' }), 'replace');
        return Promise.resolve();
      }
      if (isOpen || session === null) {
        if (route.workspace !== null && editor.workspace !== route.workspace) editor.setWorkspace(route.workspace);
        return Promise.resolve();
      }
      const id = route.id;
      return enqueue(async () => {
        if (currentProject() === id || session === null) return;
        const current = parseRoute(win.location.hash);
        if (current.kind !== 'project' || current.id !== id) return;
        opening = id;
        openError = null;
        notify();
        try {
          if (currentProject() !== null) await session.close();
          await session.open(id);
          // The address's workspace wins over the remembered one; without one, the remembered one is used.
          const now = parseRoute(win.location.hash);
          if (now.kind === 'project' && now.id === id) {
            const ed = deps.editor().getState();
            const ws = now.workspace ?? ed.workspace;
            if (ed.workspace !== ws) ed.setWorkspace(ws);
            opening = null;
            writeHash(formatRoute({ ...now, workspace: ws }), 'replace');
          }
        } catch (err) {
          openError = err;
          deps.onError?.(null, err, openOrigin);
        } finally {
          openOrigin = 'route';
          opening = null;
          notify();
        }
      });
    }
    if (route.kind === 'library' && currentProject() !== null && session !== null) {
      const s = session;
      return enqueue(async () => {
        if (currentProject() === null) return;
        try {
          await s.close();
        } catch (err) {
          deps.onError?.(null, err, 'route');
        }
        notify();
      });
    }
    return Promise.resolve();
  };

  const onHashChange = (): void => {
    void sync();
  };

  const nav: ShellNavService = {
    get hash() {
      return normalizeHash(win.location.hash);
    },
    get opening() {
      return opening;
    },
    get openError() {
      return openError;
    },
    route: () => parseRoute(win.location.hash),
    subscribe(cb) {
      listeners.add(cb);
      return () => {
        listeners.delete(cb);
      };
    },
    async openProject(id, workspace) {
      const route = parseRoute(win.location.hash);
      // No workspace asked for: the open project's, else the one remembered with the project (bare address).
      const ws = workspace ?? (currentProject() === id ? deps.editor().getState().workspace : null);
      const previous = normalizeHash(win.location.hash);
      const target = normalizeHash(
        ws === null ? `#/p/${encodeURIComponent(id)}` : formatRoute({ kind: 'project', id, workspace: ws }),
      );
      // A new attempt forgets the last failure (an attempt for this project still running answers this call).
      if (opening !== id && openError !== null) {
        openError = null;
        notify();
      }
      if (target !== previous) {
        openOrigin = 'open';
        writeHash(target, route.kind === 'project' && route.id === id ? 'replace' : 'push');
      } else if (opening !== id && currentProject() !== id) {
        // Already at this address (reload, typed link, Back) after a failed open: the hash does not change, so ask
        // the session again directly ("Try again").
        openOrigin = 'open';
        void sync();
      }
      await chain;
      if (openError !== null && currentProject() !== id) {
        // Could not open: go back to where the user was (the opener shows why).
        const err = openError;
        if (normalizeHash(win.location.hash) !== previous)
          win.history.replaceState(null, '', `${win.location.pathname}${win.location.search}${previous}`);
        lastHash = normalizeHash(win.location.hash);
        notify();
        // A PersistError object (code + message) reaches the opener unchanged.
        throw err as Error;
      }
    },
    async closeProject() {
      const s = session;
      if (s !== null && currentProject() !== null) await enqueue(() => s.close());
      writeHash('#/', 'push');
      await chain;
    },
    goLibrary() {
      writeHash('#/', 'push');
    },
    goSettings(section) {
      const onSettings = parseRoute(win.location.hash).kind === 'settings';
      writeHash(formatRoute({ kind: 'settings', section: section ?? null }), onSettings ? 'replace' : 'push');
    },
    setWorkspace(ws) {
      const editor = deps.editor().getState();
      if (editor.workspace !== ws) editor.setWorkspace(ws);
      const route = parseRoute(win.location.hash);
      if (route.kind === 'project' && route.workspace !== ws)
        writeHash(formatRoute({ ...route, workspace: ws }), 'replace');
    },
    async retryRoute() {
      const route = parseRoute(win.location.hash);
      if (route.kind !== 'project' || currentProject() === route.id) return;
      if (opening !== null) {
        await chain;
        return;
      }
      openError = null;
      openOrigin = 'route';
      notify();
      await sync();
    },
    attachSession(s) {
      session = s;
      void sync();
      return () => {
        if (session === s) session = null;
      };
    },
    start() {
      if (stop !== null) return stop;
      win.addEventListener('hashchange', onHashChange);
      win.addEventListener('popstate', onHashChange);
      const offEditor = deps.editor().subscribe((state, prev) => {
        if (state.workspace === prev.workspace) return;
        // A project being opened restores its view state: the address decides once it is open.
        if (opening !== null) return;
        const route = parseRoute(win.location.hash);
        if (route.kind === 'project' && route.workspace !== state.workspace && state.projectId === route.id) {
          writeHash(formatRoute({ ...route, workspace: state.workspace }), 'replace');
        }
      });
      void sync();
      const s = (): void => {
        win.removeEventListener('hashchange', onHashChange);
        win.removeEventListener('popstate', onHashChange);
        offEditor();
        stop = null;
      };
      stop = s;
      return s;
    },
  };
  return nav;
}
